import type { Locale, Widget } from './common/@types'
import { LOCALES, WIDGET_MODES } from './common/constants'
import { SCRIPT_URL, WIDGET_URL } from './constants/environment'
import styles from './styles.scss?inline'
import buildElement from './utils/buildElement'
import IframeMessage from './utils/IframeMessage'

declare global {
  interface HTMLElementEventMap {
    set_widget: CustomEvent<Widget>
  }
}

const getValidLocale = (locale: string | undefined): Locale => {
  return LOCALES.find(validLocale => validLocale === locale) ?? 'en-US'
}

const initializePreviewWidget = async (
  scriptElement: HTMLElement,
  parentElement: Element,
): Promise<void> => {
  const { title, titleIcon, primaryColor, locale, openingMessage, openingAvatarUrl } =
    scriptElement.dataset

  const previewMarkup = (await import('./templates/preview.html?raw')).default

  let processedMarkup = previewMarkup
    .replaceAll('{appId}', 'ccn-widget:preview')
    .replaceAll('{SCRIPT_URL}', SCRIPT_URL)
    .replaceAll('{iframeSrc}', `${WIDGET_URL}/preview`)
    .replaceAll('{title_icon}', titleIcon || `${SCRIPT_URL}/ai-avatar.svg`)

  if (title) {
    processedMarkup = processedMarkup.replaceAll('{title}', title)
  }

  const host = document.createElement('span')
  host.id = 'ccn-widget:preview'

  const container = buildElement<HTMLDivElement>(processedMarkup.replaceAll('{rules}', styles))
  const shadowRoot = host.attachShadow({ mode: 'open' })
  shadowRoot.appendChild(container)

  if (primaryColor) {
    container.style.setProperty('--primary', primaryColor)
  }

  const mainElement = container.querySelector('.ccn-widget__main')
  const iframe = container.querySelector('iframe')

  if (!mainElement || !iframe) {
    throw new Error('Missing Elements: Main, Iframe')
  }

  const iframeMessaging = new IframeMessage(iframe)

  const previewWidget = {
    id: 'preview',
    type: 'widget',
    attributes: {
      mode: WIDGET_MODES.Preview,
      locale: getValidLocale(locale),
      title: title || null,
      title_icon: titleIcon || null,
      primary_color: primaryColor || null,
      opening: {
        message: openingMessage || null,
        avatar_url: openingAvatarUrl || null,
      },
    },
  }

  iframeMessaging.postWidget(previewWidget)

  parentElement.appendChild(host)

  scriptElement.addEventListener('set_widget', event => {
    const newPreviewWidget = event.detail

    if (newPreviewWidget.attributes.primary_color) {
      container.style.setProperty('--primary', newPreviewWidget.attributes.primary_color)
    } else {
      container.style.removeProperty('--primary')
    }

    const headerIconElement = container.querySelector<HTMLImageElement>('#ccn-widget-header-icon')
    if (headerIconElement) {
      headerIconElement.src =
        newPreviewWidget.attributes.title_icon || `${SCRIPT_URL}/ai-avatar.svg`
    }

    iframeMessaging.postWidget({
      ...previewWidget,
      attributes: {
        ...previewWidget.attributes,
        ...newPreviewWidget.attributes,
        locale: getValidLocale(newPreviewWidget.attributes.locale),
      },
    })
  })
}

export default initializePreviewWidget
